import {ExpandMore} from '@mui/icons-material';
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography} from '@mui/material';
import {useState} from 'react';
import {ExamModule, ModuleTopic, Progress} from '@cp/model';
import CourseCategoryTopicCard from './CourseCategoryTopicCard';
import calculateAverageScore from './util/calculateAverageScore';
import calculateAverageStudyProgress from './util/calculateAverageStudyProgress';

interface CourseCategoryGroupProps {
  category: string;
  topics: ModuleTopic[];
  subjectColor: {bg: string; border: string; icon: string};
  relatedScores: Record<string, number>;
  progress?: Progress;
  onCreatePracticeSession?: (topic: string, difficulty: number) => Promise<void>;
  examModule: ExamModule;
}

export default function CourseCategory({
  category,
  topics,
  subjectColor,
  relatedScores,
  progress,
  onCreatePracticeSession,
  examModule,
}: CourseCategoryGroupProps) {
  const [expanded, setExpanded] = useState(false);

  const averageScore = calculateAverageScore(topics, relatedScores);
  const averageStudyProgress = calculateAverageStudyProgress(topics, progress);

  const handleChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      sx={{boxShadow: 3, borderRadius: 2, overflow: 'hidden'}}
      data-testid="course-category-accordion"
      data-category={category}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          bgcolor: subjectColor.bg,
          '&:hover': {
            bgcolor: `${subjectColor.bg}DD`,
          },
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
          },
        }}
        data-testid="course-category-accordion-summary"
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, width: '100%'}}>
          <Typography variant="h5" sx={{fontSize: '2rem'}}>
            {subjectColor.icon}
          </Typography>
          <Box sx={{flex: 1}}>
            <Typography
              variant="h6"
              sx={{fontWeight: 600, color: subjectColor.border}}
              data-testid="course-category-name"
            >
              {category}
            </Typography>
            <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center'}}>
              <Typography variant="body2" color="text.secondary" data-testid="course-category-topics-count">
                {topics.length} {topics.length === 1 ? 'topic' : 'topics'}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-testid="course-category-average-score">
                Avg Score: {averageScore.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-testid="course-category-average-study-progress">
                Avg Study: {averageStudyProgress.toFixed(0)}%
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>
      {expanded && (
        <AccordionDetails sx={{p: 3}} data-testid="course-category-accordion-details">
          <Grid container spacing={2}>
            {topics.map((topic) => (
              <Grid size={{xs: 12, sm: 6, md: 4}} key={topic.topic}>
                <CourseCategoryTopicCard
                  topic={topic}
                  subjectColor={subjectColor}
                  progressScore={relatedScores[topic.topic] || 0}
                  progress={progress}
                  onCreatePracticeSession={onCreatePracticeSession}
                  examModule={examModule}
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      )}
    </Accordion>
  );
}
